/**
 * views/reporting/sections/StickyFooter.jsx - the sticky footer and its
 * whole-report export.
 * Rendered by calling it from ReportingView.jsx with the values it reads,
 * so it renders the same DOM the view used to build inline.
 */
import { Icon } from '../../../components/ui/Icon';
import { fmt } from '../../../utils/format';
import { exportCsv } from '../../../utils/exportCsv';
import * as XLSX from 'xlsx';

export function renderStickyFooter({
  yearLabel,
  kpiOpenValue,
  kpiWonValue,
  kpiLostValue,
  kpiWinMargin,
  kpiWinRate,
  projectsThisYear,
  bidsByYear,
  pipelineByCustomer,
  winRateByCustomer,
  revTrendData,
  statusDonut,
  topBids,
  selectedYear,
}) {
  return (
    <>
      {/* STICKY FOOTER */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: '#fff',
          borderTop: '1px solid #E8E8E8',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
          padding: '12px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 100,
          marginTop: 20,
        }}
      >
        <span style={{ fontSize: 11, color: '#5A5A5A' }}>GlobalOps Reporting · {yearLabel}</span>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={() => {
              const kpiRow = [
                {
                  'Open Value': fmt(kpiOpenValue),
                  'Won Value': fmt(kpiWonValue),
                  'Lost Value': fmt(kpiLostValue),
                  'Win Margin': kpiWinMargin != null ? `${kpiWinMargin.toFixed(2)}%` : '-',
                  'Win Rate': `${kpiWinRate}%`,
                  'Total Bids': projectsThisYear.length,
                },
              ];
              const bidsRows = bidsByYear.map((d) => ({ Year: d.year, Won: d.Won, Lost: d.Lost, Open: d.Open }));
              const pipeRows = pipelineByCustomer.map((d) => ({ Customer: d.name, Value: d.value }));
              const wrRows = winRateByCustomer.map((d) => ({ Customer: d.name, 'Win Rate': d.winRate, Won: d.won, Lost: d.lost }));
              const revRows = revTrendData.map((d) => ({ Period: d.period, 'Won Value': d.wonValue, 'Open Value': d.openValue }));
              const statusRows = statusDonut.map((d) => ({ Status: d.name, Count: d.value }));
              const allRows = [];
              allRows.push({ Section: '--- KPIs ---' });
              kpiRow.forEach((r) => allRows.push(r));
              allRows.push({ Section: '' });
              allRows.push({ Section: '--- Bid History ---' });
              bidsRows.forEach((r) => allRows.push(r));
              allRows.push({ Section: '' });
              allRows.push({ Section: '--- Pipeline by Customer ---' });
              pipeRows.forEach((r) => allRows.push(r));
              allRows.push({ Section: '' });
              allRows.push({ Section: '--- Win Rate by Customer ---' });
              wrRows.forEach((r) => allRows.push(r));
              allRows.push({ Section: '' });
              allRows.push({ Section: '--- Revenue Trend ---' });
              revRows.forEach((r) => allRows.push(r));
              allRows.push({ Section: '' });
              allRows.push({ Section: '--- Status Breakdown ---' });
              statusRows.forEach((r) => allRows.push(r));
              exportCsv('globalops_full_report.csv', allRows);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              borderRadius: 6,
              border: '1px solid #E0E0E0',
              background: '#fff',
              fontSize: 11,
              fontWeight: 600,
              color: '#555',
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            <Icon name="download" size={12} color="#555" /> Export Full Report (CSV)
          </button>
          <button
            onClick={() => {
              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet([
                  {
                    'Open Value': fmt(kpiOpenValue),
                    'Won Value': fmt(kpiWonValue),
                    'Lost Value': fmt(kpiLostValue),
                    'Win Margin': kpiWinMargin != null ? `${kpiWinMargin.toFixed(2)}%` : '-',
                    'Win Rate': `${kpiWinRate}%`,
                    'Total Bids': projectsThisYear.length,
                  },
                ]),
                'KPIs'
              );
              XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(bidsByYear.map((d) => ({ Year: d.year, Won: d.Won, Lost: d.Lost, Open: d.Open }))),
                'Bid History'
              );
              XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(pipelineByCustomer.map((d) => ({ Customer: d.name, Value: d.value }))),
                'Pipeline'
              );
              XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(
                  revTrendData.map((d) => ({ Period: d.period, 'Won Value': d.wonValue, 'Open Value': d.openValue }))
                ),
                'Revenue Trend'
              );
              XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(
                  topBids.map((p) => ({
                    'Project Name': p.name || p.description || '',
                    Customer: p.customer,
                    'Quote Date': p.quoteDate || '',
                    'Quoted Price': parseFloat(p.quotedPrice || 0),
                    Status: p.status,
                  }))
                ),
                'Top Open Bids'
              );
              XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(statusDonut.map((d) => ({ Status: d.name, Count: d.value }))),
                'Status'
              );
              XLSX.utils.book_append_sheet(
                wb,
                XLSX.utils.json_to_sheet(
                  winRateByCustomer.map((d) => ({ Customer: d.name, 'Win Rate': d.winRate, Won: d.won, Lost: d.lost }))
                ),
                'Win Rate'
              );
              XLSX.writeFile(wb, `globalops_report_${selectedYear}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '7px 14px',
              borderRadius: 6,
              border: 'none',
              background: '#1B5E20',
              fontSize: 11,
              fontWeight: 700,
              color: '#fff',
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            <Icon name="download" size={12} color="#fff" /> Export as Excel
          </button>
        </div>
      </div>
    </>
  );
}
